"use client"
import React, { useState, useEffect } from "react"
import { toast } from "react-toastify"
import { addPaymentMethod } from "../../../../../services/billing"
import { useBilling } from "../../../../../context/BillingContext"

const AddPaymentModal = ({ show, onClose, data }) => {
  const initialValue = {
    number: "",
    expiry_month: "",
    expiry_year: "",
    cvv: "",
    name: "",
    is_default: false,
  }
  const [formValue, setFormValue] = useState(initialValue)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const { refetchPaymentMethods } = useBilling();

  useEffect(() => {
    if (data) {
      setFormValue((prev) => ({
        ...prev,
        number: `•••• •••• •••• ${data?.card?.last4}`,
        expiry_month: data?.card?.exp_month,
        expiry_year: data?.card?.exp_year,
        cvv: data?.card?.cvv || 123,
        name: data?.card?.name || 'John Doe',
        is_default: data?.is_default,
      }))
    }
  }, [data])

  // ✅ Utility: Validate individual field
  const validateField = (name, value) => {
    let error = ""

    switch (name) {
      case "number":
        const cleanNumber = value.replace(/\s/g, "")
        if (!cleanNumber) error = "Card number is required"
        else if (!/^\d{16}$/.test(cleanNumber)) error = "Enter a valid 16-digit card number"
        break

      case "expiry_month":
        if (!value) error = "Expiry month is required"
        else if (!/^(0[1-9]|1[0-2])$/.test(value)) error = "Invalid month (01-12)"
        break

      case "expiry_year":
        if (!value) error = "Expiry year is required"
        else {
          const currentYear = new Date().getFullYear() % 100 // last 2 digits
          if (!/^\d{2}$/.test(value)) error = "Invalid year (YY)"
          else if (parseInt(value) < currentYear) error = "Card expired"
        }
        break

      case "cvv":
        if (!value) error = "CVV is required"
        else if (!/^\d{3,4}$/.test(value)) error = "Invalid CVV"
        break

      case "name":
        if (!value.trim()) error = "Cardholder name is required"
        break

      default:
        break
    }
    return error
  }


  if (!show) return null


  // ✅ Handle change with live validation
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    let val = type === "checkbox" ? checked : value

    // Auto-format card number with spaces
    if (name === "number") {
      val = val.replace(/\D/g, "").replace(/(.{4})/g, "$1 ").trim()
    }

    setFormValue((prev) => ({ ...prev, [name]: val }))

    // validate current field
    const error = validateField(name, val)
    setErrors((prev) => ({ ...prev, [name]: error }))
  }


  // ✅ Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = {};
    Object.keys(formValue).forEach((field) => {
      if (field !== "is_default") {
        const error = validateField(field, formValue[field]); // ✅ pehle se bana hua helper use karo
        if (error) formErrors[field] = error;
      }
    });

    setErrors(formErrors);
    if (Object.keys(formErrors).length > 0) {
      return;
    }

    try {
      setLoading(true)
      const payload = {
        type: "card",
        card: {
          ...formValue,
          number: formValue.number.replace(/\s/g, ""),
        },
        upi: null
      }
      const response = await addPaymentMethod(payload)
      await refetchPaymentMethods();
      toast.success(response?.data?.message || "Payment method added successfully", {
        position: "bottom-right",
      })

      await handleCloseModal()
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || err?.message || "Failed to add payment method"
      toast.error(errorMessage, { position: "bottom-right" })
    } finally {
      setLoading(false)
    }
  }

  const handleCloseModal = async () => {
    await setFormValue(initialValue);
    await setErrors({})
    await setLoading(false);
    await onClose();
  }

  return (
    <div
      className="modal d-flex align-items-center justify-content-center"
      style={{
        display: "flex",
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 1052,
      }}
      onClick={handleCloseModal}
    >
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-semibold">Add Payment Method</h5>
            <div className="modal-close-btn">
              <button type="button" className="btn-close" onClick={handleCloseModal}></button>
            </div>
          </div>
          <div className="modal-body">
            <form >
              {/* Card Number */}
              <div className="mb-3">
                <label className="form-label">Card Number</label>
                <input
                  type="text"
                  name="number"
                  value={formValue.number}
                  onChange={handleChange}
                  maxLength={19}
                  className={`form-control ${errors.number ? "is-invalid" : formValue.number ? "is-valid" : ""}`}
                  placeholder="1234 5678 9012 3456"
                />
                {errors.number && <div className="invalid-feedback">{errors.number}</div>}
              </div>

              {/* Expiry + CVV */}
              <div className="row g-3 mb-3">
                <div className="col-4">
                  <label className="form-label">Month</label>
                  <input
                    type="text"
                    name="expiry_month"
                    value={formValue.expiry_month}
                    onChange={handleChange}
                    maxLength={2}
                    className={`form-control ${errors.expiry_month ? "is-invalid" : formValue.expiry_month ? "is-valid" : ""}`}
                    placeholder="MM"
                  />
                  {errors.expiry_month && <div className="invalid-feedback">{errors.expiry_month}</div>}
                </div>
                <div className="col-4">
                  <label className="form-label">Year</label>
                  <input
                    type="text"
                    name="expiry_year"
                    value={formValue.expiry_year}
                    onChange={handleChange}
                    maxLength={2}
                    className={`form-control ${errors.expiry_year ? "is-invalid" : formValue.expiry_year ? "is-valid" : ""}`}
                    placeholder="YY"
                  />
                  {errors.expiry_year && <div className="invalid-feedback">{errors.expiry_year}</div>}
                </div>
                <div className="col-4">
                  <label className="form-label">CVC</label>
                  <input
                    type="text"
                    name="cvv"
                    value={formValue.cvv}
                    onChange={handleChange}
                    maxLength={4}
                    className={`form-control ${errors.cvv ? "is-invalid" : formValue.cvv ? "is-valid" : ""}`}
                    placeholder="123"
                  />
                  {errors.cvv && <div className="invalid-feedback">{errors.cvv}</div>}
                </div>
              </div>

              {/* Cardholder Name */}
              <div className="mb-3">
                <label className="form-label">Cardholder Name</label>
                <input
                  type="text"
                  name="name"
                  value={formValue.name}
                  onChange={handleChange}
                  className={`form-control ${errors.name ? "is-invalid" : formValue.name ? "is-valid" : ""}`}
                  placeholder="John Doe"
                />
                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </div>

              {/* Default checkbox */}
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="is_default"
                  checked={formValue.is_default}
                  onChange={handleChange}
                  id="defaultPayment"
                />
                <label className="form-check-label" htmlFor="defaultPayment">
                  Set as default payment method
                </label>
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary flex-fill" onClick={handleCloseModal}>
              Cancel
            </button>
            <button
              className="btn plan-detail-card-btn flex-fill"
              type="submit"
              onClick={handleSubmit}
            >
              {loading ? "Adding..." : "Add Card"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddPaymentModal
